import { Link } from "react-router-dom";
import {
  Phone,
  PhoneCall,
  ClipboardList,
  Car,
  ShieldCheck,
  Warehouse,
  Wrench,
  FileText,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import Seo from "@/components/Seo";

const STEPS = [
  {
    icon: PhoneCall,
    title: "Call us or start your claim online",
    text: "Reach us on 1800 957 474 or start your claim any time – it takes a few minutes on your phone.",
  },
  {
    icon: ClipboardList,
    title: "Fill in and sign your claim form",
    text: "Complete and sign your claim form on your phone – including accident details, a diagram and photos.",
  },
  {
    icon: Car,
    title: "We get you back on the road",
    text: "We arrange your replacement car, secure storage for your damaged vehicle and the repairs.",
  },
  {
    icon: ShieldCheck,
    title: "We recover your losses",
    text: "We deal with the at-fault party's insurer and recover your losses from them – not from you.",
  },
];

const SERVICES = [
  {
    icon: FileText,
    title: "Accident claims recovery",
    text: "We lodge and manage your whole claim against the at-fault party's insurer, and negotiate the settlement for you.",
    to: "/services#claims-recovery",
    testid: "home-service-card-claims",
  },
  {
    icon: Car,
    title: "Replacement vehicles",
    text: "A replacement car from our own rental fleet keeps you moving while your vehicle is off the road.",
    to: "/services#replacement-vehicles",
    testid: "home-service-card-replacement",
  },
  {
    icon: Warehouse,
    title: "Vehicle storage",
    text: "Your damaged vehicle is stored securely at our own premises while your claim is underway.",
    to: "/services#storage",
    testid: "home-service-card-storage",
  },
  {
    icon: Wrench,
    title: "Repairs",
    text: "Assessment, quoting and repairs to pre-accident condition – with our repairer or your chosen repairer.",
    to: "/services#repairs",
    testid: "home-service-card-repairs",
  },
];

const WHY = [
  "We claim the costs of your claim from the at-fault party's insurer – not from you.",
  "Our own rental fleet – a replacement vehicle keeps you on the road while yours is being repaired.",
  "Our own secure storage – your damaged vehicle is kept safely at our premises.",
  "We deal with the insurer for you – lodging, negotiating, and engaging legal representatives if needed.",
];

export default function Home() {
  return (
    <>
      <Seo
        title="Not-at-fault accident claims | Reliance Motor Claims Recovery"
        description="Not at fault in a car accident? We manage your claim, replacement hire vehicle, storage and repairs, and claim the costs from the at-fault party's insurer. Call 1800 957 474."
        path="/"
      />

      {/* Hero */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <p className="inline-block font-saira font-semibold text-xs sm:text-sm tracking-widest uppercase text-ink border-l-4 border-road pl-3">
                Not-at-fault accident claims
              </p>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ink leading-[1.08]">
                Not at fault? We handle your claim –{" "}
                <span className="underline decoration-road decoration-4 underline-offset-8">
                  and recover the costs from the at-fault insurer.
                </span>
              </h1>
              <p className="mt-6 text-base sm:text-lg leading-relaxed text-steel max-w-2xl">
                If the accident wasn't your fault, we manage your claim against the at-fault
                driver's insurer from start to finish – lodging the claim, dealing with the
                insurer and negotiating your settlement – while keeping you on the road with a
                replacement vehicle from our own fleet.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="/claim"
                  className="inline-flex items-center justify-center gap-2 min-h-[52px] px-8 bg-ink text-white font-saira font-bold text-base sm:text-lg rounded hover:bg-black transition-colors"
                  data-testid="hero-start-claim-button"
                >
                  Start your claim
                  <ArrowRight className="size-5" aria-hidden="true" />
                </a>
                <a
                  href="tel:1800957474"
                  className="inline-flex items-center justify-center gap-2 min-h-[52px] px-8 bg-mist text-ink font-saira font-bold text-base sm:text-lg rounded hover:bg-[#E2E5E9] transition-colors"
                  data-testid="hero-call-button"
                >
                  <Phone className="size-5" aria-hidden="true" />
                  Call 1800 957 474
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-mist rounded-2xl p-6 sm:p-8 border-l-4 border-road">
                <ShieldCheck className="size-10 sm:size-12 text-ink" strokeWidth={1.75} aria-hidden="true" />
                <p className="mt-4 font-saira font-semibold text-xl text-ink">
                  After an accident, the last thing you need is paperwork.
                </p>
                <ul className="mt-5 space-y-3">
                  {WHY.slice(0, 3).map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-steel">
                      <CheckCircle2 className="size-5 mt-0.5 shrink-0 text-ink" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <p className="font-saira font-semibold text-xs sm:text-sm tracking-widest uppercase text-steel border-l-4 border-road pl-3">
            How it works
          </p>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-ink max-w-2xl">
            Four simple steps – we do the heavy lifting
          </h2>
          <ol className="mt-10 sm:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step, i) => (
              <li
                key={step.title}
                className="bg-white rounded-lg p-6 sm:p-7 flex flex-col gap-4 border border-transparent hover:border-road/40 transition-colors"
                data-testid={`how-it-works-step-${i + 1}`}
              >
                <div className="flex items-center justify-between">
                  <step.icon className="size-8 sm:size-9 text-ink" strokeWidth={1.75} aria-hidden="true" />
                  <span className="font-saira font-bold text-2xl text-road" aria-hidden="true">
                    {i + 1}
                  </span>
                </div>
                <h3 className="font-saira font-semibold text-lg text-ink">{step.title}</h3>
                <p className="text-sm sm:text-base text-steel leading-relaxed">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Services overview */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="font-saira font-semibold text-xs sm:text-sm tracking-widest uppercase text-steel border-l-4 border-road pl-3">
                Our services
              </p>
              <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-ink">
                Everything handled, end to end
              </h2>
            </div>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 min-h-[48px] font-saira font-semibold text-ink hover:underline decoration-road decoration-2 underline-offset-4"
              data-testid="home-services-view-all"
            >
              View all services <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s) => (
              <Link
                key={s.title}
                to={s.to}
                className="group bg-mist p-6 sm:p-7 rounded-lg border border-transparent hover:border-road/40 transition-colors flex flex-col gap-4"
                data-testid={s.testid}
              >
                <s.icon className="size-8 sm:size-9 text-ink" strokeWidth={1.75} aria-hidden="true" />
                <h3 className="font-saira font-semibold text-lg text-ink">{s.title}</h3>
                <p className="text-sm sm:text-base text-steel leading-relaxed flex-1">{s.text}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink group-hover:underline decoration-road decoration-2 underline-offset-4">
                  Learn more <ArrowRight className="size-4" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <p className="font-saira font-semibold text-xs sm:text-sm tracking-widest uppercase text-white/60 border-l-4 border-road pl-3">
            Why choose us
          </p>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-2xl">
            Built to take the stress out of your claim
          </h2>
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 max-w-4xl">
            {WHY.map((item, i) => (
              <li key={item} className="flex items-start gap-3" data-testid={`why-choose-item-${i + 1}`}>
                <CheckCircle2 className="size-6 mt-0.5 shrink-0 text-road" aria-hidden="true" />
                <span className="text-base sm:text-lg text-white/85 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="bg-mist rounded-2xl px-6 py-12 sm:px-12 sm:py-16 border-l-8 border-road">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-ink max-w-2xl">
              Been in an accident? Start your claim now.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-steel max-w-2xl leading-relaxed">
              It takes a few minutes on your phone. If you'd rather talk first, call us and
              we'll walk you through it.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href="/claim"
                className="inline-flex items-center justify-center gap-2 min-h-[52px] px-8 bg-ink text-white font-saira font-bold text-base sm:text-lg rounded hover:bg-black transition-colors"
                data-testid="cta-band-start-claim-button"
              >
                Start your claim
                <ArrowRight className="size-5" aria-hidden="true" />
              </a>
              <a
                href="tel:1800957474"
                className="inline-flex items-center justify-center gap-2 min-h-[52px] px-8 bg-white text-ink font-saira font-bold text-base sm:text-lg rounded border border-ink/10 hover:bg-ink hover:text-white transition-colors"
                data-testid="cta-band-call-button"
              >
                <Phone className="size-5" aria-hidden="true" />
                Call 1800 957 474
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
